import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Image,
  Spinner,
  Modal,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import api from "../api";
import { useAuth } from "../AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import {
  isValidTZ,
  isValidHetPey,
  validatePhone,
} from "../utils/fieldsValidator";
import { aggregateStats } from "../utils/Stats";
import { bytesToSize } from "../utils/bytesSize";

const UserSettings = () => {
  const { t } = useTranslation();
  const { user, setUser, events } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // --- State Management ---
  const searchParams = new URLSearchParams(location.search);
  const initialTab = searchParams.get("tab") || "personal";
  const [activeTab, setActiveTab] = useState(initialTab);

  const [navOpen, setNavOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  // Personal & Company Info
  const [firstname, setFirstname] = useState(user?.firstname || "");
  const [lastname, setLastname] = useState(user?.lastname || "");
  const [companyName, setCompanyName] = useState(user?.company_name || "");
  const [logoPreview, setLogoPreview] = useState(
    `${process.env.REACT_APP_MODEL_ENDPOINT}/user_logo?user_id=${
      user?.user_id
    }&t=${Date.now()}`
  );
  const [logoFile, setLogoFile] = useState(null);
  const [watermarkEnabled, setWatermarkEnabled] = useState(user?.watermark_enabled ?? true);
  const [defaultUploadMode, setDefaultUploadMode] = useState(user?.default_upload_mode ?? "compress");

  // Language
  const [language, setLanguage] = useState(user?.language || "en");
  useEffect(() => {
    if (user?.language) setLanguage(user.language);
  }, [user?.language]);

  // Social URLs
  const [facebook, setFacebook] = useState(user?.social_urls?.fb || "");
  const [instagram, setInstagram] = useState(user?.social_urls?.insta || "");
  const [website, setWebsite] = useState(user?.social_urls?.website || "");

  // Plans (needed for Billing tab)
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  // --- Effects ---
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await api.get("/plans");
        if (res.data.success) setPlans(res.data.data);
      } catch (err) {
        console.error("Error fetching plans", err);
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  // Check if anything changed (personal + social only)
  const isModified =
    firstname !== user?.firstname ||
    lastname !== user?.lastname ||
    companyName !== user?.company_name ||
    language !== (user?.language || "en") ||
    facebook !== (user?.social_urls?.fb || "") ||
    instagram !== (user?.social_urls?.insta || "") ||
    website !== (user?.social_urls?.website || "") ||
    logoFile !== null ||
    watermarkEnabled !== (user?.watermark_enabled ?? true) ||
    defaultUploadMode !== (user?.default_upload_mode ?? "compress");

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      await api.put(`/user/${user.user_id}`, {
        firstname,
        lastname,
        company_name: companyName,
        language,
        watermark_enabled: watermarkEnabled,
        default_upload_mode: defaultUploadMode,
        social_urls: { fb: facebook, insta: instagram, website },
      });

      if (logoFile) {
        const formData = new FormData();
        formData.append("user_id", user.user_id);
        formData.append("logo", logoFile);
        await api.post(`/user/logo?user_id=${user.user_id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      setUser({
        ...user,
        firstname,
        lastname,
        company_name: companyName,
        language,
        watermark_enabled: watermarkEnabled,
        default_upload_mode: defaultUploadMode,
        social_urls: { fb: facebook, insta: instagram, website },
      });

      i18n.changeLanguage(language);
      setLogoFile(null);
      setStatus("success");
    } catch (err) {
      console.error(err);
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const renderContent = () => {
    const commonProps = { loading, isModified, handleSave };

    switch (activeTab) {
      case "personal":
        return (
          <PersonalInfos
            {...commonProps}
            data={{
              firstname,
              lastname,
              companyName,
              logoPreview,
              userEmail: user?.email,
              language,
            }}
            setters={{
              setFirstname,
              setLastname,
              setCompanyName,
              setLanguage,
              handleLogoChange,
            }}
          />
        );
      case "gallery":
        return (
          <GallerySettings
            {...commonProps}
            data={{ watermarkEnabled, defaultUploadMode }}
            setters={{ setWatermarkEnabled, setDefaultUploadMode }}
          />
        );
      case "social":
        return (
          <SocialLinks
            {...commonProps}
            data={{ facebook, instagram, website }}
            setters={{ setFacebook, setInstagram, setWebsite }}
          />
        );
      case "billing":
        return (
          <Billing
            user={user}
            plans={plans}
            loadingPlans={loadingPlans}
            navigate={navigate}
            setUser={setUser}
          />
        );
      case "usage":
        return <UsageTab user={user} events={events ?? []} />;
      default:
        return null;
    }
  };

  const tabLabels = {
    personal: t("settings.tab_personal"),
    gallery: t("settings.tab_gallery"),
    social: t("settings.tab_social"),
    billing: t("settings.tab_billing"),
    usage: t("settings.tab_usage"),
  };

  useEffect(() => {
    if (!status) return;
    const timer = setTimeout(() => setStatus(null), 3500);
    return () => clearTimeout(timer);
  }, [status]);

  const handleSelectTab = (k) => {
    setActiveTab(k);
    setNavOpen(false);
  };

  return (
    <>
    <style>{`
      @keyframes slideInToast {
        from { opacity: 0; transform: translateY(10px) scale(0.98); }
        to   { opacity: 1; transform: translateY(0)   scale(1);    }
      }
    `}</style>
    <Container fluid className="mt-4">

      {/* Mobile toggle bar */}
      <div
        className="d-md-none d-flex align-items-center justify-content-between px-3 py-2 mb-2"
        style={{ background: "#fff", borderBottom: "1.5px solid #e0e0e0", cursor: "pointer" }}
        onClick={() => setNavOpen((o) => !o)}
      >
        <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#111", letterSpacing: "0.01em" }}>
          {navOpen ? `✕  ${t("settings.close")}` : `☰  ${t("settings.nav_toggle")}`}
        </span>
      </div>

      <Row>
        <Col md={3} lg={2} className={navOpen ? "d-block" : "d-none d-md-block"}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", padding: "4px 0" }}>
            {[
              {
                key: "personal",
                label: t("settings.tab_personal"),
                icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
                bg: "#eef2ff", color: "#6366f1", borderColor: "#c7d2fe",
              },
              {
                key: "gallery",
                label: t("settings.tab_gallery"),
                icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="2"/><circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/><path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
                bg: "#f0fdfa", color: "#0d9488", borderColor: "#99f6e4",
              },
              {
                key: "social",
                label: t("settings.tab_social"),
                icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="18" cy="5" r="3" stroke="currentColor" strokeWidth="2"/><circle cx="6" cy="12" r="3" stroke="currentColor" strokeWidth="2"/><circle cx="18" cy="19" r="3" stroke="currentColor" strokeWidth="2"/><path d="M8.59 13.51l6.83 3.98M15.41 6.51L8.59 10.49" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
                bg: "#ecfdf5", color: "#10b981", borderColor: "#a7f3d0",
              },
              {
                key: "billing",
                label: t("settings.tab_billing"),
                icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="2" y="5" width="20" height="14" rx="3" stroke="currentColor" strokeWidth="2"/><path d="M2 10h20" stroke="currentColor" strokeWidth="2"/></svg>,
                bg: "#fdf2f8", color: "#ec4899", borderColor: "#fbcfe8",
              },
              {
                key: "usage",
                label: t("settings.tab_usage"),
                icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="3" y="12" width="4" height="9" rx="1" fill="currentColor"/><rect x="10" y="7" width="4" height="14" rx="1" fill="currentColor"/><rect x="17" y="3" width="4" height="18" rx="1" fill="currentColor"/></svg>,
                bg: "#fff7ed", color: "#f59e0b", borderColor: "#fed7aa",
              },
            ].map(({ key, label, icon, bg, color, borderColor }) => {
              const active = activeTab === key;
              return (
                <button
                  key={key}
                  onClick={() => handleSelectTab(key)}
                  style={{
                    display: "flex", alignItems: "center", gap: "10px",
                    padding: "9px 12px", borderRadius: "10px",
                    border: `1px solid ${active ? borderColor : "transparent"}`,
                    background: active ? bg : "transparent",
                    cursor: "pointer", width: "100%", textAlign: "left",
                    transition: "all 0.15s",
                    borderLeft: active ? `3px solid ${color}` : "3px solid transparent",
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = "#f9fafb"; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}
                >
                  <div style={{
                    width: 26, height: 26, borderRadius: "7px",
                    background: active ? bg : "#f3f4f6",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, color: active ? color : "#9ca3af",
                    transition: "all 0.15s",
                  }}>
                    {icon}
                  </div>
                  <span style={{ fontSize: "13px", fontWeight: active ? 600 : 400, color: active ? color : "#374151" }}>
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </Col>

        <Col md={9} lg={10} className={navOpen ? "d-none d-md-block" : ""}>
          <Card className="shadow-sm">
            <Card.Body>{renderContent()}</Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>

    {/* Toast notification */}
    {status && (
      <div style={{
        position: "fixed", bottom: "28px", right: "28px", zIndex: 9999,
        display: "flex", alignItems: "center", gap: "12px",
        padding: "14px 16px", borderRadius: "12px",
        background: "#fff",
        boxShadow: "0 4px 24px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)",
        borderLeft: `3px solid ${status === "success" ? "#10b981" : "#ef4444"}`,
        minWidth: "260px", maxWidth: "360px",
        animation: "slideInToast 0.22s ease",
      }}>
        <div style={{
          width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
          background: status === "success" ? "#f0fdf4" : "#fef2f2",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {status === "success" ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M20 6L9 17l-5-5" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          )}
        </div>
        <span style={{ fontSize: "13px", fontWeight: 600, color: "#111827", flex: 1 }}>
          {status === "success" ? t("settings.save_success") : t("settings.save_error")}
        </span>
        <button
          onClick={() => setStatus(null)}
          style={{
            background: "none", border: "none", color: "#9ca3af",
            cursor: "pointer", fontSize: "18px", lineHeight: 1,
            padding: "0 0 0 4px", display: "flex", alignItems: "center",
          }}
        >
          ×
        </button>
      </div>
    )}
    </>
  );
};

/* --- SECTIONS --- */

const FieldLabel = ({ children }) => (
  <div style={{ fontSize: "10px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "5px" }}>
    {children}
  </div>
);

const SectionHeader = ({ icon, title, iconBg = "#eef2ff" }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "24px" }}>
    <div style={{ width: 36, height: 36, borderRadius: "10px", background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {icon}
    </div>
    <span style={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>{title}</span>
  </div>
);

const SaveButton = ({ loading, isModified, handleSave, label }) => (
  <button
    type="button"
    onClick={handleSave}
    disabled={loading || !isModified}
    style={{
      display: "inline-flex", alignItems: "center", gap: "7px",
      padding: "9px 20px", borderRadius: "10px",
      border: "none",
      background: loading || !isModified ? "#e5e7eb" : "#6366f1",
      color: loading || !isModified ? "#9ca3af" : "#fff",
      fontSize: "13px", fontWeight: 600, cursor: loading || !isModified ? "not-allowed" : "pointer",
      transition: "all 0.15s",
    }}
  >
    {loading ? <Spinner size="sm" style={{ borderColor: "#fff", borderRightColor: "transparent" }} /> : label}
  </button>
);

const PersonalInfos = ({ data, setters, loading, isModified, handleSave }) => {
  const { t } = useTranslation();
  return (
    <div style={{ maxWidth: 560 }}>
      <SectionHeader
        title={t("settings.personal.title")}
        icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke="#6366f1" strokeWidth="2"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="#6366f1" strokeWidth="2" strokeLinecap="round"/></svg>}
      />

      <Form>
        {/* Name row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "14px" }}>
          <div>
            <FieldLabel>{t("settings.personal.first_name")}</FieldLabel>
            <Form.Control
              type="text"
              value={data.firstname}
              onChange={(e) => setters.setFirstname(e.target.value)}
              style={{ borderRadius: "8px", fontSize: "13px", border: "1px solid #e5e7eb" }}
            />
          </div>
          <div>
            <FieldLabel>{t("settings.personal.last_name")}</FieldLabel>
            <Form.Control
              type="text"
              value={data.lastname}
              onChange={(e) => setters.setLastname(e.target.value)}
              style={{ borderRadius: "8px", fontSize: "13px", border: "1px solid #e5e7eb" }}
            />
          </div>
        </div>

        <div style={{ marginBottom: "14px" }}>
          <FieldLabel>{t("settings.personal.company_name")}</FieldLabel>
          <Form.Control
            type="text"
            value={data.companyName}
            onChange={(e) => setters.setCompanyName(e.target.value)}
            style={{ borderRadius: "8px", fontSize: "13px", border: "1px solid #e5e7eb" }}
          />
        </div>

        <div style={{ marginBottom: "14px" }}>
          <FieldLabel>{t("settings.personal.email")}</FieldLabel>
          <Form.Control
            type="text" readOnly disabled value={data.userEmail}
            style={{ borderRadius: "8px", fontSize: "13px", border: "1px solid #e5e7eb", background: "#f9fafb", color: "#9ca3af" }}
          />
        </div>

        <div style={{ marginBottom: "14px" }}>
          <FieldLabel>{t("settings.personal.language")}</FieldLabel>
          <div style={{ display: "inline-flex", background: "#f1f3f5", borderRadius: "8px", padding: "3px", gap: "2px" }}>
            {[
              { code: "en", label: "English" },
              { code: "fr", label: "Français" },
              { code: "he", label: "עברית" },
            ].map(({ code, label }) => (
              <button
                key={code} type="button"
                onClick={() => setters.setLanguage(code)}
                style={{
                  padding: "5px 14px", borderRadius: "6px", border: "none",
                  fontSize: "0.84rem", fontWeight: data.language === code ? 600 : 400,
                  color: data.language === code ? "#212529" : "#6c757d",
                  background: data.language === code ? "#ffffff" : "transparent",
                  boxShadow: data.language === code ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                  cursor: "pointer", transition: "all 0.15s ease", userSelect: "none",
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Logo */}
        <div style={{ marginBottom: "14px" }}>
          <FieldLabel>{t("settings.personal.company_logo")}</FieldLabel>
          <div style={{
            display: "flex", alignItems: "center", gap: "14px",
            padding: "12px 14px", borderRadius: "10px",
            border: "1px solid #e5e7eb", background: "#fafafa",
          }}>
            <Image
              src={data.logoPreview}
              alt="Logo"
              height="52"
              style={{ borderRadius: "8px", border: "1px solid #e5e7eb", objectFit: "contain" }}
            />
            <div style={{ flex: 1 }}>
              <Form.Control
                type="file" accept="image/*"
                onChange={setters.handleLogoChange}
                style={{ fontSize: "12px", border: "1px solid #e5e7eb", borderRadius: "8px" }}
              />
            </div>
          </div>
        </div>

        <SaveButton loading={loading} isModified={isModified} handleSave={handleSave} label={t("settings.personal.save_btn")} />
      </Form>
    </div>
  );
};


const GallerySettings = ({ data, setters, loading, isModified, handleSave }) => {
  const { t } = useTranslation();
  return (
    <div style={{ maxWidth: 560 }}>
      <SectionHeader
        title={t("settings.gallery.title")}
        iconBg="#f0fdfa"
        icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="3" stroke="#0d9488" strokeWidth="2"/><circle cx="8.5" cy="8.5" r="1.5" fill="#0d9488"/><path d="M21 15l-5-5L5 21" stroke="#0d9488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
      />

      {/* Default Upload Quality */}
      <div style={{ marginBottom: "20px" }}>
        <FieldLabel>{t("settings.gallery.default_upload_mode")}</FieldLabel>
        <div style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "10px" }}>
          {t("settings.gallery.default_upload_mode_hint")}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          {[
            { value: "original", labelKey: "settings.gallery.mode_original", hintKey: "settings.gallery.mode_original_hint" },
            { value: "compress", labelKey: "settings.gallery.mode_compress", hintKey: "settings.gallery.mode_compress_hint" },
          ].map(({ value, labelKey, hintKey }) => {
            const active = data.defaultUploadMode === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => setters.setDefaultUploadMode(value)}
                style={{
                  padding: "12px 14px", borderRadius: "10px",
                  border: `1.5px solid ${active ? "#0d9488" : "#e5e7eb"}`,
                  background: active ? "#f0fdfa" : "#fafafa",
                  textAlign: "left", cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "3px" }}>
                  <div style={{
                    width: 14, height: 14, borderRadius: "50%",
                    border: `2px solid ${active ? "#0d9488" : "#d1d5db"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    {active && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#0d9488" }} />}
                  </div>
                  <span style={{ fontSize: "13px", fontWeight: 600, color: active ? "#0d9488" : "#374151" }}>
                    {t(labelKey)}
                  </span>
                </div>
                <div style={{ fontSize: "11px", color: "#9ca3af", paddingLeft: "22px" }}>
                  {t(hintKey)}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Watermark */}
      <div style={{
        marginBottom: "24px", padding: "12px 14px", borderRadius: "10px",
        border: "1px solid #e5e7eb", background: "#fafafa",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px",
      }}>
        <div>
          <div style={{ fontSize: "13px", fontWeight: 600, color: "#374151" }}>{t("settings.gallery.watermark")}</div>
          <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>{t("settings.gallery.watermark_hint")}</div>
        </div>
        <Form.Check
          type="switch"
          id="watermark-enabled-switch"
          label={data.watermarkEnabled ? t("settings.gallery.watermark_on") : t("settings.gallery.watermark_off")}
          checked={data.watermarkEnabled}
          onChange={(e) => setters.setWatermarkEnabled(e.target.checked)}
          style={{ fontSize: "13px" }}
        />
      </div>

      <SaveButton loading={loading} isModified={isModified} handleSave={handleSave} label={t("settings.gallery.save_btn")} />
    </div>
  );
};

const SocialInputRow = ({ icon, iconBg, iconColor, label, value, onChange, placeholder }) => (
  <div style={{ marginBottom: "12px" }}>
    <FieldLabel>{label}</FieldLabel>
    <div style={{
      display: "flex", alignItems: "center",
      border: "1px solid #e5e7eb", borderRadius: "10px",
      background: "#fff", overflow: "hidden",
      transition: "box-shadow 0.15s",
    }}
      onFocusCapture={e => e.currentTarget.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.12)"}
      onBlurCapture={e => e.currentTarget.style.boxShadow = "none"}
    >
      <div style={{
        width: 40, height: 40, background: iconBg,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, borderRight: "1px solid #e5e7eb",
      }}>
        {icon}
      </div>
      <input
        type="url"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          flex: 1, border: "none", outline: "none",
          padding: "0 12px", fontSize: "13px", color: "#374151",
          background: "transparent", height: "40px",
        }}
      />
    </div>
  </div>
);

const SocialLinks = ({ data, setters, loading, isModified, handleSave }) => {
  const { t } = useTranslation();
  return (
    <div style={{ maxWidth: 520 }}>
      <SectionHeader
        title={t("settings.tab_social")}
        icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><circle cx="18" cy="5" r="3" stroke="#6366f1" strokeWidth="2"/><circle cx="6" cy="12" r="3" stroke="#6366f1" strokeWidth="2"/><circle cx="18" cy="19" r="3" stroke="#6366f1" strokeWidth="2"/><path d="M8.59 13.51l6.83 3.98M15.41 6.51L8.59 10.49" stroke="#6366f1" strokeWidth="2" strokeLinecap="round"/></svg>}
      />

      <Form>
        <SocialInputRow
          label={t("settings.social.facebook")}
          value={data.facebook}
          onChange={(e) => setters.setFacebook(e.target.value)}
          placeholder="https://facebook.com/yourpage"
          iconBg="#eff6ff"
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="#1877f2"><path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.413c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.234 2.686.234v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/></svg>}
        />

        <SocialInputRow
          label={t("settings.social.instagram")}
          value={data.instagram}
          onChange={(e) => setters.setInstagram(e.target.value)}
          placeholder="https://instagram.com/yourhandle"
          iconBg="#fdf2f8"
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="#e1306c"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>}
        />

        <SocialInputRow
          label={t("settings.social.website")}
          value={data.website}
          onChange={(e) => setters.setWebsite(e.target.value)}
          placeholder="https://yourwebsite.com"
          iconBg="#eef2ff"
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#6366f1" strokeWidth="2"/><path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20" stroke="#6366f1" strokeWidth="2" strokeLinecap="round"/></svg>}
        />

        <div style={{ marginTop: "20px" }}>
          <SaveButton loading={loading} isModified={isModified} handleSave={handleSave} label={t("settings.social.save_btn")} />
        </div>
      </Form>
    </div>
  );
};


const InvoiceTable = ({ invoices }) => {
  const { t } = useTranslation();
  const thStyle = { fontSize: "10px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", paddingBottom: "10px", fontFamily: "inherit" };
  const tdStyle = { fontSize: "13px", color: "#374151", padding: "8px 0", borderTop: "1px solid #f3f4f6" };
  return (
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <th style={thStyle}>{t("settings.invoice.date")}</th>
          <th style={thStyle}>{t("settings.invoice.total")}</th>
          <th style={thStyle}>{t("settings.invoice.status")}</th>
          <th style={thStyle}>{t("settings.invoice.actions")}</th>
        </tr>
      </thead>
      <tbody>
        {invoices.map((inv) => (
          <tr key={inv.payment_ref}>
            <td style={tdStyle}>
              {new Date(inv.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </td>
            <td style={tdStyle}>{parseFloat(inv.amount).toFixed(2)} {inv.currency}</td>
            <td style={{ ...tdStyle, textTransform: "capitalize" }}>{inv.status}</td>
            <td style={tdStyle}>
              {inv.receipt ? (
                <a href={inv.receipt} target="_blank" rel="noreferrer" style={{ fontSize: "13px", color: "#6366f1", fontWeight: 600, textDecoration: "none" }}>{t("settings.invoice.view")}</a>
              ) : "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const Billing = ({ user, plans, loadingPlans, navigate, setUser }) => {
  const { t } = useTranslation();
  const { refreshUser, user: contextUser } = useAuth();
  const location = useLocation();
  const [localUser, setLocalUser] = useState(contextUser ?? user);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelDowngradeLoading, setCancelDowngradeLoading] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const [showAllInvoices, setShowAllInvoices] = useState(false);

  // Card update flow
  const [showCardUpdate, setShowCardUpdate] = useState(false);
  const [showCardUpdateForm, setShowCardUpdateForm] = useState(false);
  const [cardUpdateLoading, setCardUpdateLoading] = useState(false);
  const [cardUpdateStatus, setCardUpdateStatus] = useState(null); // null | "success" | "error"
  const [cardUpdateError, setCardUpdateError] = useState(null);
  const [cardUpdatePayRef, setCardUpdatePayRef] = useState(null);
  const [cardPayLoading, setCardPayLoading] = useState(false);
  const cardIframeRef = useRef(null);
  const cardPollRef = useRef(null);
  const cardPendingUrl = useRef(null);

  // Billing info for card update
  const billingInfos = localUser?.billing_infos;
  const [cardFullName, setCardFullName] = useState(billingInfos?.full_name || `${localUser?.firstname || ""} ${localUser?.lastname || ""}`.trim());
  const [cardEmail, setCardEmail] = useState(billingInfos?.email || localUser?.email || "");
  const [cardPhone, setCardPhone] = useState(billingInfos?.phone || localUser?.phone || "");
  const [cardTZ, setCardTZ] = useState(billingInfos?.tz_number || "");
  const [cardPhoneError, setCardPhoneError] = useState("");

  useEffect(() => {
    setLocalUser(contextUser ?? user);
  }, [contextUser, user]);

  // On redirect back from AllPay (?card_updated=1), poll until webhook is processed then refresh
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("card_updated") !== "1") return;
    const payRef = sessionStorage.getItem("cardUpdatePayRef");
    if (!payRef) return;
    let active = true;
    const poll = setInterval(async () => {
      if (!active) return;
      try {
        const res = await api.get(`/payment/status/${payRef}`);
        const status = res.data?.status;
        if (status === "refunded" || status === "paid") {
          clearInterval(poll);
          sessionStorage.removeItem("cardUpdatePayRef");
          if (active) await refreshUser();
        } else if (status === "failed") {
          clearInterval(poll);
          sessionStorage.removeItem("cardUpdatePayRef");
        }
      } catch (e) {
        clearInterval(poll);
      }
    }, 2000);
    return () => {
      active = false;
      clearInterval(poll);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/payment/invoices")
      .then((r) => setInvoices(r.data.invoices || []))
      .catch((err) => console.error("Error fetching invoices", err))
      .finally(() => setLoadingInvoices(false));
  }, []);

  const allpayErrorMsgRef = useRef(null);

  // Capture full AllPay paymentResult payload (including error_msg) from postMessage
  useEffect(() => {
    const handler = (event) => {
      const data = event.data;
      if (data?.action === "paymentResult" && data?.ok === false && data?.error_msg) {
        allpayErrorMsgRef.current = data.error_msg;
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // Initialize AllPay HF when card update iframe is shown
  useEffect(() => {
    if (!showCardUpdate || cardUpdateStatus) return;

    const initAllpay = () => {
      if (!cardIframeRef.current || !window.AllpayPayment) return;
      if (cardPendingUrl.current) {
        cardIframeRef.current.src = cardPendingUrl.current;
        cardPendingUrl.current = null;
      }
      window.CardAllpay = new window.AllpayPayment({
        iframeId: "cardUpdateIframe",
        onSuccess: () => {
          console.log("✅ Card update AllPay onSuccess");
          startCardPolling();
        },
        onError: (err) => {
          console.error("❌ Card update AllPay error", err);
          setCardUpdateError(allpayErrorMsgRef.current || null);
          allpayErrorMsgRef.current = null;
          setCardUpdateStatus("error");
          stopCardPolling();
        },
      });
    };

    if (window.AllpayPayment) {
      initAllpay();
    } else {
      let script = document.querySelector('script[src="https://allpay.to/js/allpay-hf.js"]');
      if (!script) {
        script = document.createElement("script");
        script.src = "https://allpay.to/js/allpay-hf.js";
        script.async = true;
        document.body.appendChild(script);
      }
      script.addEventListener("load", initAllpay);
      return () => script.removeEventListener("load", initAllpay);
    }
  }, [showCardUpdate, cardUpdateStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopCardPolling();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startCardPolling = (ref = cardUpdatePayRef) => {
    stopCardPolling();
    const refToUse = ref || cardUpdatePayRef;
    if (!refToUse) return;
    cardPollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/payment/status/${refToUse}`);
        const status = res.data?.status;
        if (status === "refunded" || status === "paid") {
          stopCardPolling();
          if (refreshUser) await refreshUser();
          setCardUpdateStatus("success");
        } else if (status === "failed") {
          stopCardPolling();
          setCardUpdateStatus("error");
        }
      } catch (err) {
        console.error("Card update polling error", err);
      }
    }, 3000);
  };

  const stopCardPolling = () => {
    if (cardPollRef.current) {
      clearInterval(cardPollRef.current);
      cardPollRef.current = null;
    }
  };

  const handleUpdateCard = async () => {
    setCardUpdateLoading(true);
    setCardUpdateStatus(null);
    setCardUpdateError(null);
    setCardPayLoading(false);
    setShowCardUpdate(false);
    setShowCardUpdateForm(false);
    try {
      const res = await api.post("/payment/create-card-update-session", {
        full_name: cardFullName,
        email: cardEmail,
        phone: cardPhone || undefined,
        client_tehudat: cardTZ || undefined,
      });
      if (res.data.success) {
        setCardUpdatePayRef(res.data.payment_ref);
        cardPendingUrl.current = res.data.payment_url;
        sessionStorage.setItem("cardUpdatePayRef", res.data.payment_ref);
        setShowCardUpdate(true);
      }
    } catch (err) {
      console.error("Failed to create card update session", err);
      setCardUpdateStatus("error");
    } finally {
      setCardUpdateLoading(false);
    }
  };

  const handleCardPay = () => {
    if (!window.CardAllpay) {
      console.error("AllPay not initialized");
      return;
    }
    if (!cardIframeRef.current?.src) {
      console.error("Card iframe src missing");
      return;
    }
    setCardPayLoading(true);
    startCardPolling(cardUpdatePayRef);
    window.CardAllpay.pay();
  };

  const handleCancelCardUpdate = () => {
    stopCardPolling();
    setShowCardUpdate(false);
    setShowCardUpdateForm(false);
    setCardUpdateStatus(null);
    setCardUpdateError(null);
    setCardUpdatePayRef(null);
    setCardPayLoading(false);
  };

  const subscription = localUser?.subscription;
  const paymentMethod = localUser?.payment_method;
  const currentPlan = plans?.find((p) => p.plan_id === subscription?.plan_id);
  const downgradePlan = plans?.find((p) => p.plan_id === subscription?.next_plan_id);

  const renewDate = subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  const hasFutureDowngrade = subscription?.next_plan_id && subscription?.current_period_end;
  const getPlanPrice = (plan, cycle) =>
    plan && cycle ? (cycle === "monthly" ? plan.monthly_price : plan.yearly_price) : null;

  const handleCancelDowngrade = async () => {
    try {
      setCancelDowngradeLoading(true);
      const { data } = await api.post("/payment/cancel_downgrade");
      if (data?.success) {
        setLocalUser((prev) => ({
          ...prev,
          subscription: { ...prev.subscription, next_plan_id: null, next_billing_cycle: null },
        }));
      }
    } catch (err) {
      console.error("Failed to cancel downgrade", err);
    } finally {
      setCancelDowngradeLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm(t("settings.billing.cancel_confirm"))) return;
    try {
      setCancelLoading(true);
      setCancelError("");
      await api.post("/payment/cancel-subscription", {});
      setLocalUser((prev) => ({
        ...prev,
        subscription: { ...prev.subscription, cancel_at_period_end: true },
      }));
      if (setUser) {
        setUser((prev) => ({
          ...prev,
          subscription: { ...prev.subscription, cancel_at_period_end: true },
        }));
      }
    } catch (err) {
      console.error("Failed to cancel subscription", err);
      setCancelError(t("settings.billing.cancel_error"));
    } finally {
      setCancelLoading(false);
    }
  };

  const sectionStyle = { borderTop: "1px solid #f3f4f6", paddingTop: "20px", marginBottom: "24px" };
  const inlineBtn = (bg, color, border = "none") => ({
    padding: "6px 14px", borderRadius: "8px", border,
    background: bg, color, fontSize: "12px", fontWeight: 600,
    cursor: "pointer", transition: "all 0.15s", display: "inline-flex", alignItems: "center", gap: "6px",
  });
  const inputStyle = { borderRadius: "8px", fontSize: "13px", border: "1px solid #e5e7eb" };

  return (
    <>
      <SectionHeader
        title={t("settings.tab_billing")}
        icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="2" y="5" width="20" height="14" rx="3" stroke="#ec4899" strokeWidth="2"/><path d="M2 10h20" stroke="#ec4899" strokeWidth="2"/></svg>}
      />

      {/* PLAN SECTION */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", ...sectionStyle }}>
        <div>
          {loadingPlans ? (
            <Spinner size="sm" />
          ) : currentPlan ? (
            <>
              <div style={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>
                {currentPlan.plan_name} {t("settings.billing.plan_suffix")}
              </div>
              {currentPlan.plan_name !== "Free" && (
                <div style={{ fontSize: "12px", color: "#9ca3af", textTransform: "capitalize", marginTop: "2px" }}>
                  {subscription?.billing_cycle}
                </div>
              )}
              {currentPlan.plan_name !== "Free" && renewDate && !subscription?.cancel_at_period_end && (
                <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "2px" }}>
                  {t("settings.billing.auto_renew", { date: renewDate })}
                </div>
              )}
              {subscription?.cancel_at_period_end && currentPlan.plan_name !== "Free" && (
                <div style={{ fontSize: "12px", color: "#f59e0b", marginTop: "2px" }}>
                  {t("settings.billing.cancel_on", { date: renewDate })}
                </div>
              )}
              {hasFutureDowngrade && (
                <div style={{
                  marginTop: "10px", padding: "10px 14px", borderRadius: "10px",
                  background: "#fffbeb", border: "1px solid #fde68a",
                  fontSize: "12px", color: "#92400e", lineHeight: 1.6,
                }}>
                  <div style={{ fontWeight: 700, marginBottom: "4px" }}>
                    {t("settings.billing.downgrade_scheduled", {
                      plan: downgradePlan?.plan_name,
                      price: getPlanPrice(downgradePlan, subscription?.next_billing_cycle),
                      cycle: subscription?.next_billing_cycle,
                    })}
                  </div>
                  <div>{t("settings.billing.effective_on")} <strong>{renewDate}</strong></div>
                  <button
                    style={{ ...inlineBtn("#fef2f2", "#ef4444", "1px solid #fecaca"), marginTop: "8px" }}
                    onClick={handleCancelDowngrade}
                    disabled={cancelDowngradeLoading}
                  >
                    {cancelDowngradeLoading ? <Spinner size="sm" /> : t("settings.billing.cancel_downgrade")}
                  </button>
                </div>
              )}
            </>
          ) : (
            <span style={{ fontSize: "13px", color: "#9ca3af" }}>{t("settings.billing.no_plan")}</span>
          )}
        </div>
        <button style={inlineBtn("#f3f4f6", "#374151", "1px solid #e5e7eb")} onClick={() => navigate("/plans")}>
          {t("settings.billing.adjust_plan")}
        </button>
      </div>

      {/* PAYMENT SECTION */}
      <div style={sectionStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <FieldLabel>{t("settings.billing.payment")}</FieldLabel>
            {paymentMethod ? (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#374151", marginTop: "4px" }}>
                <span>💳</span>
                <span style={{ textTransform: "capitalize" }}>{paymentMethod.card_brand} •••• {paymentMethod.card_mask}</span>
              </div>
            ) : (
              <span style={{ fontSize: "13px", color: "#9ca3af" }}>{t("settings.billing.no_payment_method")}</span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {cardUpdateStatus === "success" && (
              <span style={{ fontSize: "12px", color: "#10b981", fontWeight: 600 }}>{t("settings.billing.card_updated")}</span>
            )}
            {cardUpdateStatus === "error" && (
              <>
                <span style={{ fontSize: "12px", color: "#ef4444" }}>{cardUpdateError || t("settings.billing.update_failed")}</span>
                <button style={inlineBtn("#fef2f2", "#ef4444", "1px solid #fecaca")} onClick={() => { setCardUpdateStatus(null); setShowCardUpdateForm(true); }}>
                  {t("settings.billing.try_again")}
                </button>
              </>
            )}
            {subscription && !showCardUpdate && !showCardUpdateForm && !cardUpdateStatus && (
              <button style={inlineBtn("#f3f4f6", "#374151", "1px solid #e5e7eb")} onClick={() => setShowCardUpdateForm(true)} disabled={cardUpdateLoading}>
                {cardUpdateLoading ? <Spinner size="sm" /> : t("settings.billing.update")}
              </button>
            )}
            {(showCardUpdate || showCardUpdateForm) && !cardUpdateStatus && (
              <button style={{ background: "none", border: "none", fontSize: "12px", color: "#9ca3af", cursor: "pointer" }} onClick={handleCancelCardUpdate}>
                {t("settings.billing.cancel")}
              </button>
            )}
          </div>
        </div>

        {/* Card update billing form */}
        {showCardUpdateForm && !showCardUpdate && !cardUpdateStatus && (
          <div style={{ marginTop: "14px", padding: "14px", borderRadius: "10px", border: "1px solid #e5e7eb", background: "#fafafa" }}>
            <Row className="g-2">
              <Col xs={12} sm={6}>
                <FieldLabel>Full Name *</FieldLabel>
                <Form.Control size="sm" type="text" value={cardFullName} onChange={(e) => setCardFullName(e.target.value)} style={inputStyle} />
              </Col>
              <Col xs={12} sm={6}>
                <FieldLabel>Email *</FieldLabel>
                <Form.Control size="sm" type="email" value={cardEmail} onChange={(e) => setCardEmail(e.target.value)} style={inputStyle} />
              </Col>
              <Col xs={12} sm={6}>
                <FieldLabel>Phone</FieldLabel>
                <Form.Control size="sm" type="tel" value={cardPhone} onChange={(e) => { setCardPhone(e.target.value); setCardPhoneError(validatePhone(e.target.value)); }} isInvalid={!!cardPhoneError} style={inputStyle} />
                <Form.Control.Feedback type="invalid">{cardPhoneError}</Form.Control.Feedback>
              </Col>
              <Col xs={12} sm={6}>
                <FieldLabel>Tehudat Zehut</FieldLabel>
                <Form.Control size="sm" type="text" value={cardTZ} onChange={(e) => setCardTZ(e.target.value)} isInvalid={!!cardTZ && !isValidTZ(cardTZ)} style={inputStyle} />
                <Form.Control.Feedback type="invalid">Invalid TZ</Form.Control.Feedback>
              </Col>
            </Row>
            <div style={{ marginTop: "12px", display: "flex", justifyContent: "flex-end" }}>
              <button
                style={{ ...inlineBtn("#6366f1", "#fff"), opacity: (cardUpdateLoading || !cardFullName || !cardEmail || !!cardPhoneError || (!!cardTZ && !isValidTZ(cardTZ))) ? 0.5 : 1 }}
                onClick={handleUpdateCard}
                disabled={cardUpdateLoading || !cardFullName || !cardEmail || !!cardPhoneError || (!!cardTZ && !isValidTZ(cardTZ))}
              >
                {cardUpdateLoading ? <Spinner size="sm" /> : "Continue"}
              </button>
            </div>
          </div>
        )}

        {/* AllPay iframe for card update */}
        {showCardUpdate && !cardUpdateStatus && (
          <div style={{ marginTop: "14px" }}>
            <iframe id="cardUpdateIframe" ref={cardIframeRef} title="card-update" style={{ width: "100%", height: "200px", border: "none", borderRadius: "10px" }} />
            <div style={{ marginTop: "12px", display: "flex", justifyContent: "center" }}>
              <button
                style={{ ...inlineBtn("#6366f1", "#fff"), minWidth: "200px", justifyContent: "center", padding: "10px 24px", borderRadius: "10px", opacity: cardPayLoading ? 0.7 : 1 }}
                onClick={handleCardPay}
                disabled={cardPayLoading}
              >
                {cardPayLoading ? <><Spinner size="sm" /> {t("settings.billing.processing")}</> : t("settings.billing.confirm_card")}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* INVOICES SECTION */}
      <div style={sectionStyle}>
        <FieldLabel>{t("settings.billing.invoices")}</FieldLabel>
        <div style={{ marginTop: "10px" }}>
          {loadingInvoices ? (
            <Spinner size="sm" />
          ) : invoices.length === 0 ? (
            <span style={{ fontSize: "13px", color: "#9ca3af" }}>{t("settings.billing.no_invoices")}</span>
          ) : (
            <>
              <InvoiceTable invoices={invoices.slice(0, 5)} />
              {invoices.length > 5 && (
                <button
                  style={{ background: "none", border: "none", padding: 0, marginTop: "10px", fontSize: "12px", fontWeight: 600, color: "#6366f1", cursor: "pointer" }}
                  onClick={() => setShowAllInvoices(true)}
                >
                  {t("settings.billing.show_all", { count: invoices.length })}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* All invoices modal */}
      <Modal show={showAllInvoices} onHide={() => setShowAllInvoices(false)} size="lg">
        <Modal.Header closeButton style={{ borderBottom: "1px solid #f3f4f6" }}>
          <Modal.Title style={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>{t("settings.billing.all_invoices_title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <InvoiceTable invoices={invoices} />
        </Modal.Body>
      </Modal>

      {/* CANCELLATION SECTION */}
      {subscription && subscription.status !== "canceled" && !subscription.cancel_at_period_end && (
        <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: "20px" }}>
          <FieldLabel>{t("settings.billing.cancellation")}</FieldLabel>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "8px" }}>
            <span style={{ fontSize: "13px", color: "#9ca3af" }}>{t("settings.billing.cancel_plan")}</span>
            <button
              style={inlineBtn("#fef2f2", "#ef4444", "1px solid #fecaca")}
              onClick={handleCancelSubscription}
              disabled={cancelLoading}
            >
              {cancelLoading ? <Spinner size="sm" /> : t("settings.billing.cancel_btn")}
            </button>
          </div>
          {cancelError && (
            <div style={{ marginTop: "8px", background: "#fef2f2", color: "#ef4444", border: "1px solid #fecaca", borderRadius: "8px", padding: "8px 12px", fontSize: "12px" }}>
              {cancelError}
            </div>
          )}
        </div>
      )}
    </>
  );
};

const UsageTab = ({ user, events }) => {
  const { t } = useTranslation();
  const { photoStats } = aggregateStats(events);
  const limitBytes = (user?.subscription?.memory_limit_gb ?? 0) * 1024 ** 3;
  const usedBytes = photoStats.used_memory;
  const availableBytes = Math.max(limitBytes - usedBytes, 0);
  const usedPct = limitBytes > 0 ? Math.min((usedBytes / limitBytes) * 100, 100) : 0;

  const progressColor = usedPct >= 90 ? "danger" : usedPct >= 70 ? "warning" : "success";

  return (
    <>
      <SectionHeader
        title={t("settings.tab_usage")}
        icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="3" y="12" width="4" height="9" rx="1" fill="#f59e0b"/><rect x="10" y="7" width="4" height="14" rx="1" fill="#f59e0b"/><rect x="17" y="3" width="4" height="18" rx="1" fill="#f59e0b"/></svg>}
      />

      <Row className="mb-4">
        <Col xs={6}>
          <div className="text-muted small fw-semibold mb-1">{t("settings.usage.events")}</div>
          <div className="fs-4 fw-bold">{events.length}
            {user?.subscription?.max_events > 0 && (
              <span className="fs-6 text-muted fw-normal ms-1">/ {user.subscription.max_events}</span>
            )}
          </div>
        </Col>
        <Col xs={6}>
          <div className="text-muted small fw-semibold mb-1">{t("settings.usage.photos")}</div>
          <div className="fs-4 fw-bold">{photoStats.total_images.toLocaleString()}</div>
        </Col>
      </Row>

      <div className="mb-2 d-flex justify-content-between align-items-baseline">
        <div className="text-muted small fw-semibold">{t("settings.usage.storage")}</div>
        <div className="small text-muted">{bytesToSize(availableBytes)} {t("settings.usage.available")}</div>
      </div>
      <div className="progress mb-2" style={{ height: "10px" }}>
        <div
          className={`progress-bar bg-${progressColor}`}
          role="progressbar"
          style={{ width: `${usedPct}%` }}
          aria-valuenow={usedPct}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
      <div className="d-flex justify-content-between small text-muted">
        <span>{bytesToSize(usedBytes)} {t("settings.usage.used")}</span>
        <span>{limitBytes > 0 ? bytesToSize(limitBytes) : "—"} {t("settings.usage.total")}</span>
      </div>
    </>
  );
};

export default UserSettings;
